import { useLazyGetAgendaViewRecordsQuery, useCreateAgendaRecordMutation } from 'store/services/flow/agendaRecords';
import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { agendaRecordsService } from 'store/services/flow/agendaRecords';
import { useShoppingCart } from 'contexts';
import { TAG_TYPES } from 'types/services';
import type { GetViewByTypeRes, RecordAttributeRes } from 'types';
import type { CartItemType } from 'components/mui/common/Header/CartItem';

interface UseShoppingCartDataProps {
  shoppingCarts: GetViewByTypeRes['views'] | undefined;
  currentModuleId?: string;
  currentAgendaId?: string;
  recordId?: string;
}

interface UseShoppingCartDataResult {
  carts: CartItemType[];
  totalItemCount: number | undefined;
  isLoading: boolean;
  refetch: () => void;
}

export const extractAttrValue = (attr?: RecordAttributeRes) =>
  Object.values(attr || {}).find((val) => typeof val === 'object' && val !== null && 'value' in val)?.value;

export const buildCartFromRecord = (view: GetViewByTypeRes['views'][number], firstItem: { attributes: RecordAttributeRes[] }): CartItemType => {
  const productCountAttr = firstItem.attributes.find(
    (attr: RecordAttributeRes) => attr.formItem?.id === view.shoppingCartParameters?.cartItemCountFormItemId
  );
  const totalPriceAttr = firstItem.attributes.find(
    (attr: RecordAttributeRes) => attr.formItem?.id === view.shoppingCartParameters?.cartTotalFormItemId
  );
  const descriptionAttr = firstItem.attributes.find(
    (attr: RecordAttributeRes) => attr.formItem?.id === view.shoppingCartParameters?.cartDescriptionFormItemId
  );

  const productCount = String(extractAttrValue(productCountAttr) || 0);
  const totalPrice = String(extractAttrValue(totalPriceAttr) || 0);
  const description = String(extractAttrValue(descriptionAttr) || view.viewName);

  return {
    id: view.viewId,
    name: description,
    productCount,
    totalPrice,
    agendaId: view.parentId,
    moduleId: view.moduleId,
  };
};

export const buildEmptyCart = (view: GetViewByTypeRes['views'][number]): CartItemType => ({
  id: view.viewId,
  name: view.viewName,
  productCount: '0',
  totalPrice: '0',
  agendaId: view.parentId,
  moduleId: view.moduleId,
});

export const useShoppingCartData = ({
  shoppingCarts,
  currentModuleId,
  currentAgendaId,
  recordId,
}: UseShoppingCartDataProps): UseShoppingCartDataResult => {
  const dispatch = useDispatch();
  const [getShoppingCartRecords, queryState] = useLazyGetAgendaViewRecordsQuery();
  const [createAgendaRecord] = useCreateAgendaRecordMutation();
  const [carts, setCarts] = useState<CartItemType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const creatingRecordsRef = useRef<Set<string>>(new Set());
  const { subscribe } = useShoppingCart();

  const fetchCarts = useCallback(
    async (skipCache = false) => {
      if (!shoppingCarts) {
        setCarts([]);
        setIsLoading(false);
        return;
      }

      const createDefaultCartRecord = async (view: GetViewByTypeRes['views'][number]) => {
        const descriptionFormItemId = view.shoppingCartParameters?.cartDescriptionFormItemId;

        if (descriptionFormItemId && !creatingRecordsRef.current.has(view.parentId)) {
          creatingRecordsRef.current.add(view.parentId);

          await createAgendaRecord({
            agendaId: view.parentId,
            body: {
              status: 'Active',
              attributes: [
                {
                  formItemId: descriptionFormItemId,
                  automaticSequenceAttributeInput: null,
                  buttonAttributeInput: null,
                  checkboxAttributeInput: null,
                  commentAttributeInput: null,
                  computedAttributeInput: null,
                  dateAttributeInput: null,
                  emailAttributeInput: null,
                  fileUploadAttributeInput: null,
                  numberAttributeInput: null,
                  phoneNumberAttributeInput: null,
                  radioButtonAttributeInput: null,
                  recordsAttributeInput: null,
                  richTextAttributeInput: null,
                  tablesAttributeInput: null,
                  textAttributeInput: { value: view.viewName },
                  usersAttributeInput: null,
                },
              ],
            },
          });
        }
      };

      const fetchOrCreateCart = async (view: GetViewByTypeRes['views'][number]): Promise<CartItemType> => {
        const { data: { items } = {} } = await getShoppingCartRecords(
          {
            params: {
              viewId: view.viewId,
              agendaId: view.parentId,
              page: 1,
              pageSize: 1,
              search: '',
              orderBy: '',
              orderDesc: true,
            },
            recordIds: [],
            valueFormItems: [],
            filters: [],
          },
          skipCache
        );

        const firstItem = items?.[0];

        if (!firstItem) {
          await createDefaultCartRecord(view);
          return buildEmptyCart(view);
        }

        return buildCartFromRecord(view, firstItem);
      };

      setIsLoading(true);
      try {
        const cartPromises = shoppingCarts.filter((view) => view.moduleId === currentModuleId).map(fetchOrCreateCart);

        const results = await Promise.all(cartPromises);
        setCarts(results);
      } finally {
        setIsLoading(false);
      }
    },
    // currentAgendaId and recordId need to be in the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getShoppingCartRecords, shoppingCarts, createAgendaRecord, currentModuleId, currentAgendaId, recordId]
  );

  const refetch = useCallback(async () => {
    dispatch(agendaRecordsService.util.invalidateTags([{ type: TAG_TYPES.RECORDS, id: 'LIST' }]));
    // This timeout is necessary so that we refetch after backend has processed the update
    await new Promise((resolve) => setTimeout(resolve, 200));
    await fetchCarts(true);
  }, [dispatch, fetchCarts]);

  useEffect(() => {
    const unsubscribe = subscribe(refetch);
    return unsubscribe;
  }, [subscribe, refetch]);

  useEffect(() => {
    void fetchCarts();
  }, [fetchCarts]);

  const totalItemCount = useMemo(() => {
    const allAreNumbers = carts.every((cart) => {
      const num = Number(cart.productCount);
      return !isNaN(num) && isFinite(num);
    });

    if (!allAreNumbers) {
      return undefined;
    }

    return carts.reduce((acc, cart) => acc + Number(cart.productCount), 0);
  }, [carts]);

  return {
    carts,
    totalItemCount,
    isLoading: isLoading || queryState?.isFetching || false,
    refetch,
  };
};
